import {
  VERDICT_LABEL,
  disagreementPins,
  type AdversarialRun,
  type ReviewAssessment,
  type TourStop,
} from '../lib/adversarialReview.ts'
import { Button } from './Button.tsx'
import { Coachmark } from './Coachmark.tsx'
import { DisagreementNote } from './DisagreementNote.tsx'

/**
 * The guided tour of an adversarial review (#541): one balloon per
 * disagreement, pointing at the row it is about on the real conferência.
 *
 * The same coach mark that shows a pharmacist where to answer a divergence,
 * used as a sequence. Each stop frames the disputed row, says what the review
 * thinks is wrong with it and why, and asks whether the review was right.
 *
 * Dimmed like that hint, with two differences. The receita stays lit beside
 * the row, because deciding who is right means reading it; and the page
 * behind stays usable, so it can be zoomed while the balloon is up.
 */

/** The card of the step on screen: where a stop points when it has no row. */
export const REVIEW_CARD_SELECTOR = '[data-review-card]'

/** The receita pane, left undimmed beside whatever a stop points at. */
export const REVIEW_RECEITA_SELECTOR = '[data-review-receita]'

export interface AdversarialReviewTourProps {
  readonly run: AdversarialRun
  readonly tour: readonly TourStop[]
  /** Which stop is on screen. */
  readonly at: number
  readonly onAssess: (disagreement: string, assessment: ReviewAssessment | null) => void
  readonly onGo: (at: number) => void
  /** Out of the tour, back to the conferência as it is. */
  readonly onLeave: () => void
}

export function AdversarialReviewTour({
  run,
  tour,
  at,
  onAssess,
  onGo,
  onLeave,
}: AdversarialReviewTourProps) {
  const stop = tour[at]
  if (stop === undefined) return null
  const { disagreement } = stop
  const last = at === tour.length - 1

  return (
    <Coachmark
      // Remounted per stop, so each one measures its own balloon.
      key={disagreement.id}
      selector={tourSelectors(stop)}
      title={`${VERDICT_LABEL[disagreement.verdict]} · ${String(at + 1)} de ${String(tour.length)}`}
      passive
      also={REVIEW_RECEITA_SELECTOR}
      width={420}
      onDismiss={onLeave}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="small"
            variant="secondary"
            tone="neutral"
            disabled={at === 0}
            onClick={() => onGo(at - 1)}
          >
            Anterior
          </Button>
          <Button
            size="small"
            variant="primary"
            tone="brand"
            onClick={() => (last ? onLeave() : onGo(at + 1))}
          >
            {last ? 'Concluir revisão' : 'Próxima'}
          </Button>
          <Button
            size="small"
            variant="tertiary"
            tone="neutral"
            className="ml-auto"
            onClick={onLeave}
          >
            Sair
          </Button>
        </div>
      }
    >
      <DisagreementNote
        disagreement={disagreement}
        assessment={run.assessments[disagreement.id]}
        onAssess={(assessment) => onAssess(disagreement.id, assessment)}
        bare
      />
    </Coachmark>
  )
}

/**
 * What a stop points at, most exact first: the disputed row, then the card it
 * is on. The card is only for a dispute that names no finding and cites no
 * block any row was drawn from -- there is then no row to say it is about.
 */
export function tourSelectors(stop: TourStop): readonly string[] {
  const rows = disagreementPins(stop.step, stop.disagreement)
    // Rows drawn by the card. A pin made of cited blocks alone is a rectangle
    // on the receita, with no row on this side to point at.
    .filter((pin) => !pin.key.startsWith('review:'))
    .map((pin) => `[data-review-row="${cssString(pin.key)}"]`)
  return [...rows, REVIEW_CARD_SELECTOR]
}

/** A value safe inside a double-quoted attribute selector. */
function cssString(value: string): string {
  return value.replace(/["\\]/g, '\\$&')
}
